import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import './AnalyticsExport.css';

interface FilterPreset {
  id: string;
  name: string;
  filters: AnalyticsFilters;
}

interface AnalyticsFilters {
  dateRange?: { start: Date; end: Date };
  projectId?: string;
  sceneId?: string;
  disciplineType?: 'music' | 'dance' | 'drama';
  studentId?: string;
}

interface AnalyticsExportProps {
  projectId: string;
  teacherId: string;
  onClose?: () => void;
}

export const AnalyticsExport: React.FC<AnalyticsExportProps> = ({ projectId, teacherId, onClose }) => {
  const [filters, setFilters] = useState<AnalyticsFilters>({ projectId });
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'png' | 'html'>('csv');
  const [exportType, setExportType] = useState<'class-stats' | 'student-status' | 'technical-scores' | 'growth-trends' | 'cue-conflicts'>('class-stats');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const apiBase = import.meta.env.VITE_API_URL || '/api';

  const handleFilterChange = (key: keyof AnalyticsFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const savePreset = async () => {
    if (!presetName.trim()) {
      setError('Please enter a preset name');
      return;
    }

    try {
      const response = await fetch(`${apiBase}/analytics/filter-presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacherId,
          name: presetName,
          filters
        })
      });

      if (!response.ok) throw new Error('Failed to save preset');

      const newPreset = await response.json();
      setPresets(prev => [...prev, newPreset]);
      setPresetName('');
      setSuccess('Preset saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preset');
    }
  };

  const loadPreset = (preset: FilterPreset) => {
    setFilters(preset.filters);
    setSuccess(`Loaded preset: ${preset.name}`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const deletePreset = async (presetId: string) => {
    try {
      const response = await fetch(`${apiBase}/analytics/filter-presets/${presetId}?teacherId=${teacherId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete preset');

      setPresets(prev => prev.filter(p => p.id !== presetId));
      setSuccess('Preset deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete preset');
    }
  };

  const exportData = async () => {
    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      let url = '';
      const params = new URLSearchParams();

      // Build URL based on export type
      switch (exportType) {
        case 'class-stats':
          url = `${apiBase}/analytics/class-practice-stats/${projectId}`;
          if (filters.dateRange) {
            params.append('startDate', filters.dateRange.start.toISOString());
            params.append('endDate', filters.dateRange.end.toISOString());
          }
          break;
        case 'student-status':
          url = `${apiBase}/analytics/student-practice-status/${projectId}`;
          if (filters.sceneId) params.append('sceneId', filters.sceneId);
          break;
        case 'technical-scores':
          url = `${apiBase}/analytics/technical-scores/${projectId}`;
          if (filters.disciplineType) params.append('disciplineType', filters.disciplineType);
          break;
        case 'growth-trends':
          if (!filters.studentId) {
            throw new Error('Student ID is required for growth trends export');
          }
          url = `${apiBase}/analytics/growth-trends/${filters.studentId}`;
          if (filters.projectId) params.append('projectId', filters.projectId);
          if (filters.sceneId) params.append('sceneId', filters.sceneId);
          if (filters.disciplineType) params.append('disciplineType', filters.disciplineType);
          break;
        case 'cue-conflicts':
          url = `${apiBase}/analytics/cue-conflicts/${projectId}`;
          if (filters.sceneId) params.append('sceneId', filters.sceneId);
          break;
      }

      const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error('Failed to fetch analytics data');
      const data = await response.json();

      // Export based on format
      if (exportFormat === 'png') {
        await exportToPNG(data, exportType);
      } else if (exportFormat === 'html') {
        await exportToHTML(data, exportType);
      } else {
        // Use backend export endpoint for CSV and JSON
        const exportResponse = await fetch(`${apiBase}/analytics/export`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            exportType,
            format: exportFormat,
            filters,
            data
          })
        });

        if (!exportResponse.ok) throw new Error('Failed to export data');

        if (exportFormat === 'csv') {
          const csv = await exportResponse.text();
          downloadFile(csv, `analytics-${exportType}-${Date.now()}.csv`, 'text/csv');
        } else if (exportFormat === 'json') {
          const json = await exportResponse.text();
          downloadFile(json, `analytics-${exportType}-${Date.now()}.json`, 'application/json');
        }
      }

      setSuccess('Export completed successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportToPNG = async (data: any, type: string) => {
    try {
      // Create a temporary container with the data visualization
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = '1200px';
      container.style.padding = '40px';
      container.style.backgroundColor = 'white';
      container.style.fontFamily = 'Arial, sans-serif';

      // Generate HTML content based on export type
      let html = `<h1 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px;">Analytics Report - ${type}</h1>`;
      html += `<p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>`;

      switch (type) {
        case 'class-stats':
          html += '<div style="display: flex; flex-wrap: wrap; gap: 20px; margin: 20px 0;">';
          html += `<div style="border: 1px solid #ddd; padding: 20px; border-radius: 5px;"><div style="font-size: 32px; font-weight: bold; color: #3498db;">${data.totalStudents}</div><div style="font-size: 14px; color: #7f8c8d;">Total Students</div></div>`;
          html += `<div style="border: 1px solid #ddd; padding: 20px; border-radius: 5px;"><div style="font-size: 32px; font-weight: bold; color: #3498db;">${data.studentsWhoPracticed}</div><div style="font-size: 14px; color: #7f8c8d;">Students Practiced</div></div>`;
          html += `<div style="border: 1px solid #ddd; padding: 20px; border-radius: 5px;"><div style="font-size: 32px; font-weight: bold; color: #3498db;">${data.practiceCompletionRate.toFixed(1)}%</div><div style="font-size: 14px; color: #7f8c8d;">Completion Rate</div></div>`;
          html += '</div>';
          html += '<h2 style="color: #34495e; margin-top: 30px;">Scene Completion</h2>';
          html += '<table style="width: 100%; border-collapse: collapse;"><tr style="background-color: #3498db; color: white;"><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Scene</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Students</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Rate</th></tr>';
          data.sceneStats.forEach((scene: any, i: number) => {
            const bgColor = i % 2 === 0 ? '#f2f2f2' : 'white';
            html += `<tr style="background-color: ${bgColor};"><td style="border: 1px solid #ddd; padding: 12px;">${scene.sceneTitle}</td><td style="border: 1px solid #ddd; padding: 12px;">${scene.studentsPracticed}</td><td style="border: 1px solid #ddd; padding: 12px;">${scene.completionRate.toFixed(1)}%</td></tr>`;
          });
          html += '</table>';
          break;

        case 'student-status':
          html += '<h2 style="color: #34495e;">Student Practice Status</h2>';
          html += '<table style="width: 100%; border-collapse: collapse;"><tr style="background-color: #3498db; color: white;"><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Student</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Scene</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Status</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Count</th></tr>';
          data.slice(0, 20).forEach((status: any, i: number) => {
            const bgColor = i % 2 === 0 ? '#f2f2f2' : 'white';
            html += `<tr style="background-color: ${bgColor};"><td style="border: 1px solid #ddd; padding: 12px;">${status.userName}</td><td style="border: 1px solid #ddd; padding: 12px;">${status.sceneTitle}</td><td style="border: 1px solid #ddd; padding: 12px;">${status.status}</td><td style="border: 1px solid #ddd; padding: 12px;">${status.practiceCount}</td></tr>`;
          });
          html += '</table>';
          if (data.length > 20) {
            html += `<p style="margin-top: 10px; color: #7f8c8d;">Showing first 20 of ${data.length} entries</p>`;
          }
          break;

        case 'technical-scores':
          html += '<h2 style="color: #34495e;">Technical Scores</h2>';
          html += '<table style="width: 100%; border-collapse: collapse;"><tr style="background-color: #3498db; color: white;"><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Discipline</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Avg Score</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Min</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Max</th><th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Students</th></tr>';
          data.forEach((score: any, i: number) => {
            const bgColor = i % 2 === 0 ? '#f2f2f2' : 'white';
            html += `<tr style="background-color: ${bgColor};"><td style="border: 1px solid #ddd; padding: 12px;">${score.disciplineType}</td><td style="border: 1px solid #ddd; padding: 12px;">${score.averageScore.toFixed(2)}</td><td style="border: 1px solid #ddd; padding: 12px;">${score.minScore.toFixed(2)}</td><td style="border: 1px solid #ddd; padding: 12px;">${score.maxScore.toFixed(2)}</td><td style="border: 1px solid #ddd; padding: 12px;">${score.studentCount}</td></tr>`;
          });
          html += '</table>';
          break;
      }

      container.innerHTML = html;
      document.body.appendChild(container);

      // Capture as PNG using html2canvas
      const canvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 2
      });

      document.body.removeChild(container);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `analytics-${type}-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      });
    } catch (err) {
      throw new Error('PNG export failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const exportToHTML = async (data: any, type: string) => {
    try {
      const response = await fetch(`${apiBase}/analytics/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exportType: type,
          format: 'html',
          filters,
          data
        })
      });

      if (!response.ok) throw new Error('Failed to generate HTML report');

      const html = await response.text();
      
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      } else {
        // Fallback: download as HTML file
        downloadFile(html, `analytics-${type}-${Date.now()}.html`, 'text/html');
      }
    } catch (err) {
      throw new Error('HTML export failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Load presets from backend on mount
  React.useEffect(() => {
    const loadPresets = async () => {
      try {
        const response = await fetch(`${apiBase}/analytics/filter-presets/${teacherId}`);
        if (response.ok) {
          const data = await response.json();
          setPresets(data);
        }
      } catch (err) {
        console.error('Failed to load presets:', err);
      }
    };

    loadPresets();
  }, [teacherId]);

  return (
    <div className="analytics-export">
      <div className="export-header">
        <h3>Export Analytics</h3>
        {onClose && (
          <button className="close-button" onClick={onClose}>×</button>
        )}
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {success && (
        <div className="success-message">{success}</div>
      )}

      {/* Export Type Selection */}
      <section className="export-section">
        <h4>Export Type</h4>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              value="class-stats"
              checked={exportType === 'class-stats'}
              onChange={(e) => setExportType(e.target.value as any)}
            />
            Class Practice Statistics
          </label>
          <label>
            <input
              type="radio"
              value="student-status"
              checked={exportType === 'student-status'}
              onChange={(e) => setExportType(e.target.value as any)}
            />
            Student Practice Status
          </label>
          <label>
            <input
              type="radio"
              value="technical-scores"
              checked={exportType === 'technical-scores'}
              onChange={(e) => setExportType(e.target.value as any)}
            />
            Technical Scores
          </label>
          <label>
            <input
              type="radio"
              value="growth-trends"
              checked={exportType === 'growth-trends'}
              onChange={(e) => setExportType(e.target.value as any)}
            />
            Growth Trends
          </label>
          <label>
            <input
              type="radio"
              value="cue-conflicts"
              checked={exportType === 'cue-conflicts'}
              onChange={(e) => setExportType(e.target.value as any)}
            />
            Cue Conflicts
          </label>
        </div>
      </section>

      {/* Filters */}
      <section className="export-section">
        <h4>Filters</h4>
        <div className="filters-grid">
          <div className="filter-field">
            <label>Date Range (Start)</label>
            <input
              type="date"
              value={filters.dateRange?.start.toISOString().split('T')[0] || ''}
              onChange={(e) => {
                const start = e.target.value ? new Date(e.target.value) : undefined;
                handleFilterChange('dateRange', start ? { ...filters.dateRange, start } : undefined);
              }}
            />
          </div>
          <div className="filter-field">
            <label>Date Range (End)</label>
            <input
              type="date"
              value={filters.dateRange?.end.toISOString().split('T')[0] || ''}
              onChange={(e) => {
                const end = e.target.value ? new Date(e.target.value) : undefined;
                handleFilterChange('dateRange', end ? { ...filters.dateRange, end } : undefined);
              }}
            />
          </div>
          <div className="filter-field">
            <label>Scene ID</label>
            <input
              type="text"
              placeholder="Optional"
              value={filters.sceneId || ''}
              onChange={(e) => handleFilterChange('sceneId', e.target.value || undefined)}
            />
          </div>
          <div className="filter-field">
            <label>Discipline</label>
            <select
              value={filters.disciplineType || ''}
              onChange={(e) => handleFilterChange('disciplineType', e.target.value || undefined)}
            >
              <option value="">All</option>
              <option value="music">Music</option>
              <option value="dance">Dance</option>
              <option value="drama">Drama</option>
            </select>
          </div>
          {exportType === 'growth-trends' && (
            <div className="filter-field">
              <label>Student ID (Required)</label>
              <input
                type="text"
                placeholder="Enter student ID"
                value={filters.studentId || ''}
                onChange={(e) => handleFilterChange('studentId', e.target.value || undefined)}
              />
            </div>
          )}
        </div>
      </section>

      {/* Filter Presets */}
      <section className="export-section">
        <h4>Filter Presets</h4>
        <div className="preset-controls">
          <input
            type="text"
            placeholder="Preset name"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
          />
          <button onClick={savePreset} className="save-preset-button">
            Save Current Filters
          </button>
        </div>
        {presets.length > 0 && (
          <div className="presets-list">
            {presets.map(preset => (
              <div key={preset.id} className="preset-item">
                <span className="preset-name">{preset.name}</span>
                <div className="preset-actions">
                  <button onClick={() => loadPreset(preset)} className="load-button">
                    Load
                  </button>
                  <button onClick={() => deletePreset(preset.id)} className="delete-button">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Export Format */}
      <section className="export-section">
        <h4>Export Format</h4>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              value="csv"
              checked={exportFormat === 'csv'}
              onChange={(e) => setExportFormat(e.target.value as any)}
            />
            CSV (Spreadsheet)
          </label>
          <label>
            <input
              type="radio"
              value="json"
              checked={exportFormat === 'json'}
              onChange={(e) => setExportFormat(e.target.value as any)}
            />
            JSON (Data)
          </label>
          <label>
            <input
              type="radio"
              value="png"
              checked={exportFormat === 'png'}
              onChange={(e) => setExportFormat(e.target.value as any)}
            />
            PNG (Image)
          </label>
          <label>
            <input
              type="radio"
              value="html"
              checked={exportFormat === 'html'}
              onChange={(e) => setExportFormat(e.target.value as any)}
            />
            HTML (Printable Report)
          </label>
        </div>
      </section>

      {/* Export Button */}
      <div className="export-actions">
        <button
          onClick={exportData}
          disabled={exporting}
          className="export-button"
        >
          {exporting ? 'Exporting...' : 'Export Data'}
        </button>
      </div>
    </div>
  );
};

export default AnalyticsExport;
